import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import DashboardLayout from '../../components/Layout/DashboardLayout';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import JobMatchModal from '../../components/Jobs/JobMatchModal';
import * as jobService from '../../services/jobService';
import { timeAgo } from '../../utils/formatters';
import Badge from '../../components/UI/Badge';
import { toast } from 'react-toastify';

export default function JobDetails() {
	const { id } = useParams();
	const [job, setJob] = useState(null);
	const [loading, setLoading] = useState(true);
	const [showMatch, setShowMatch] = useState(false);
	const [similar, setSimilar] = useState([]);

	useEffect(() => {
		(async () => {
			try {
				const res = await jobService.getJobById(id);
				setJob(res.job);
				const rec = await jobService.searchJobs({ keyword: res.job.title, page: 1, limit: 4 });
				setSimilar((rec.jobs || []).filter((j) => j._id !== id));
			} catch {
				toast.error('Failed to load job');
			} finally {
				setLoading(false);
			}
		})();
	}, [id]);

	if (loading) return <LoadingSpinner fullScreen />;
	if (!job) return null;

	const posted = timeAgo(job.postedDate);
	const salary = job.salaryRange ? formatSalary(job.salaryRange) : '';

	return (
		<DashboardLayout>
			<div className="space-y-6">
				<nav className="text-sm text-gray-500">
					<Link to="/jobs" className="hover:underline">
						Jobs
					</Link>{' '}
					&gt; <span className="text-gray-700">{job.title}</span>
				</nav>

				<header className="flex items-start justify-between gap-4">
					<div>
						<h1 className="text-2xl font-bold">{job.title}</h1>
						<p className="text-gray-600">
							{job.companyId?.companyName || 'Company'} • {job.location}
						</p>
						<div className="mt-2 flex items-center gap-2">
							{job.employmentType && <Badge text={job.employmentType} variant="info" />}
							{salary && <Badge text={salary} variant="neutral" />}
							<Badge text={`Posted ${posted}`} variant="neutral" />
							<Badge text={`${job.applicationsCount || 0} applicants`} variant="neutral" />
						</div>
					</div>
					<div className="flex items-center gap-2">
						<Button onClick={() => setShowMatch(true)}>Match My Resume</Button>
						<Button variant="ghost" onClick={() => toast.info('Saved')}>
							Save Job
						</Button>
						<Button variant="ghost" onClick={() => navigator.clipboard.writeText(window.location.href)}>
							Share Job
						</Button>
						<Button variant="secondary" onClick={() => toast.info('Apply link opening')}>
							Apply Now
						</Button>
					</div>
				</header>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
					<div className="lg:col-span-2 space-y-4">
						<Card title="Overview">
							<div className="grid grid-cols-2 gap-3 text-sm">
								<div>
									<p className="text-gray-500">Experience</p>
									<p className="font-medium">{job.experienceLevel}</p>
								</div>
								<div>
									<p className="text-gray-500">Location Type</p>
									<p className="font-medium">{job.locationType}</p>
								</div>
								<div>
									<p className="text-gray-500">Employment</p>
									<p className="font-medium">{job.employmentType}</p>
								</div>
								<div>
									<p className="text-gray-500">Salary</p>
									<p className="font-medium">{salary || 'N/A'}</p>
								</div>
							</div>
						</Card>
						<Card title="Job Description">
							<p className="whitespace-pre-wrap text-sm text-gray-800">{job.description}</p>
						</Card>
						{(job.responsibilities || []).length > 0 && (
							<Card title="Responsibilities">
								<ul className="list-disc ml-5 text-sm">
									{job.responsibilities.map((r, i) => (
										<li key={i}>{r}</li>
									))}
								</ul>
							</Card>
						)}
						{(job.requirements || []).length > 0 && (
							<Card title="Requirements">
								<ul className="list-disc ml-5 text-sm">
									{job.requirements.map((r, i) => (
										<li key={i}>{r}</li>
									))}
								</ul>
							</Card>
						)}
						{(job.skills || []).length > 0 && (
							<Card title="Skills">
								<div className="flex flex-wrap gap-2">
									{job.skills.map((s, i) => (
										<Badge key={i} text={s} variant="info" />
									))}
								</div>
							</Card>
						)}
					</div>
					<div className="lg:col-span-1 space-y-4">
						<Card title="Company">
							<div className="flex items-center gap-3">
								<div className="w-12 h-12 bg-gray-100 rounded" />
								<div>
									<p className="font-medium">{job.companyId?.companyName || 'Company'}</p>
									<p className="text-sm text-gray-500">{job.companyId?.industry}</p>
								</div>
							</div>
							<div className="mt-2 text-sm">
								<p>Size: {job.companyId?.size || 'N/A'}</p>
								{job.companyId?.website && (
									<p className="truncate">
										Website:{' '}
										<a href={job.companyId.website} className="text-primary-600 underline" target="_blank" rel="noreferrer">
											{job.companyId.website}
										</a>
									</p>
								)}
							</div>
						</Card>

						{similar.length > 0 && (
							<Card title="Similar Jobs">
								<ul className="space-y-2">
									{similar.map((j) => (
										<li key={j._id} className="text-sm">
											<Link className="text-primary-600 underline" to={`/jobs/${j._id}`}>
												{j.title}
											</Link>
											<p className="text-gray-500">{j.companyId?.companyName}</p>
										</li>
									))}
								</ul>
							</Card>
						)}
					</div>
				</div>
			</div>
			<JobMatchModal isOpen={showMatch} onClose={() => setShowMatch(false)} jobId={id} job={job} />
		</DashboardLayout>
	);
}

function formatSalary(sr) {
	const min = sr.min ? `$${sr.min}` : '';
	const max = sr.max ? `$${sr.max}` : '';
	const cur = sr.currency || 'USD';
	return [min, max].filter(Boolean).join(' - ') + (cur ? ` ${cur}` : '');
}


